import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Users, Headphones } from "lucide-react";
import SpaceJetNavbar from "../components/SpaceJetNavbar";
import SpaceJetParticleField from "../components/SpaceJetParticleField";
import Reveal from "../components/Reveal";
import { ThemeProvider } from "../contexts/ThemeContext";
import { contactsAPI } from "../api";

const SpaceJetNetworkContact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await contactsAPI.submit(formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (error) {
      console.error("Contact form submission failed:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Partnership email",
      details: "growth@spacejetmedia.com",
      description: "For advertiser, publisher, and partner-fit conversations",
    },
    {
      icon: Phone,
      title: "Talk to the team",
      details: "(877) 287-7822",
      description: "Speak directly with SpaceJet about fit and next steps",
    },
    {
      icon: MapPin,
      title: "Partnership HQ",
      details: "2901 W Bluegrass Blvd., Suite 200-217 Lehi, UT 84048",
      description: "Where partnership and account conversations happen",
    },
    {
      icon: Clock,
      title: "Response window",
      details: "Mon-Fri: 9AM-6PM EST",
      description: "When the partnership team is available",
    },
  ];

  const departments = [
    {
      icon: Users,
      title: "Advertiser partnerships",
      email: "growth@spacejetmedia.com",
      description: "Talk through traffic quality, growth goals, and campaign fit",
    },
    {
      icon: Headphones,
      title: "Publisher partnerships",
      email: "growth@spacejetmedia.com",
      description: "Application help, offer questions, and hands-on account support",
    },
    {
      icon: MessageSquare,
      title: "Press & brand",
      email: "growth@spacejetmedia.com",
      description: "Brand, press, and public-facing questions",
    },
  ];

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-[#050b17] text-slate-100 spacejet-texture-dots spacejet-texture-living transition-colors duration-300" style={{ "--spacejet-texture-dot-opacity": 0.105, "--spacejet-texture-grid-opacity": 0.07, "--spacejet-texture-ambient-opacity": 0.085, "--spacejet-texture-drift-duration": "50s", "--spacejet-texture-glow-duration": "35s", "--spacejet-texture-sheen-duration": "41s" }}>
        <SpaceJetNavbar />
        <SpaceJetParticleField maxParticles={44} speed={0.14} baseOpacity={0.2} />

        <section className="relative overflow-hidden py-20 md:py-24">
          <div className="absolute inset-0 spacejet-gradient-overlay" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <Reveal animation="spacejet-fade-up">
              <div className="spacejet-page-kicker mb-6">
                Contact
              </div>
              <h1 className="spacejet-page-title text-4xl font-semibold leading-tight text-slate-100 md:text-6xl">
                Talk partnership,
                <span className="block font-light text-slate-300">not formality.</span>
              </h1>
            </Reveal>

            <Reveal animation="spacejet-fade-up" delay={100}>
              <p className="spacejet-page-copy mx-auto mt-6 max-w-3xl text-xl">
                Tell us about your goals, traffic, or offer, and we’ll route you to the right team for the next step.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <Reveal animation="spacejet-scale-in">
              <div className="spacejet-surface-card rounded-2xl p-8">
                <div className="spacejet-page-kicker mb-3">Start the conversation</div>
                <h2 className="spacejet-page-title mb-6 text-3xl font-semibold text-slate-100">Share a little context.</h2>
                <p className="spacejet-page-copy mb-8">
                  We’ll follow up with the right next step for your advertiser, publisher, or partnership inquiry.
                </p>

                {submitStatus === "success" && (
                  <div className="mb-6 rounded-lg border border-emerald-700/60 bg-emerald-900/40 p-4 text-emerald-200">
                    Thank you for your message. We’ll get back to you within 24 hours.
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="mb-6 rounded-lg border border-red-700/60 bg-red-900/40 p-4 text-red-200">
                    Sorry, there was an error sending your message. Please try again.
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-300">Full name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-slate-100 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="John Doe" />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-300">Email address *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-slate-100 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="john@example.com" />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-300">Company</label>
                    <input type="text" name="company" value={formData.company} onChange={handleInputChange} className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-slate-100 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="Your company name" />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-300">Subject *</label>
                    <input type="text" name="subject" value={formData.subject} onChange={handleInputChange} required className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-slate-100 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="Advertiser partnership, publisher application, or other fit question" />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-300">Message *</label>
                    <textarea name="message" value={formData.message} onChange={handleInputChange} required rows={6} className="w-full resize-none rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-slate-100 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="Share your goals, traffic or offer type, and what you're looking for in a partner..." />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="spacejet-btn-primary group inline-flex w-full items-center justify-center rounded-lg px-8 py-4 font-semibold transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60">
                    {isSubmitting ? "Sending..." : "Start conversation"}
                    <Send className={`ml-2 h-5 w-5 transition-transform ${isSubmitting ? "animate-pulse" : "group-hover:translate-x-1"}`} />
                  </button>
                </form>
              </div>
            </Reveal>

            <div className="space-y-8">
              <Reveal animation="spacejet-fade-up">
                <div>
                  <div className="spacejet-page-kicker mb-3">Who should reach out</div>
                  <h2 className="spacejet-page-title mb-6 text-3xl font-semibold text-slate-100">Use the channel that matches your partnership need.</h2>
                  <p className="spacejet-page-copy mb-8">We keep the routing simple so you can get to the right person quickly.</p>
                </div>
              </Reveal>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <Reveal key={info.title} animation="spacejet-scale-in" delay={index * 100}>
                      <div className="spacejet-surface-card rounded-xl p-6">
                        <div className="mb-4 flex items-center">
                          <div className="mr-4 rounded-lg bg-gradient-to-r from-slate-600 to-slate-700 p-3">
                            <Icon className="h-6 w-6 text-slate-200" />
                          </div>
                          <h3 className="text-lg font-semibold text-slate-100">{info.title}</h3>
                        </div>
                        <p className="mb-2 font-medium text-slate-300">{info.details}</p>
                        <p className="text-sm text-slate-400">{info.description}</p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>

              <Reveal animation="spacejet-fade-up" delay={200}>
                <div className="mt-12">
                  <div className="spacejet-page-kicker mb-3">Team contacts</div>
                  <div className="space-y-4">
                    {departments.map((dept, index) => {
                      const Icon = dept.icon;
                      return (
                        <div key={dept.title} className="spacejet-surface-card rounded-xl p-6">
                          <div className="flex items-start">
                            <div className="mr-4 mt-1 rounded-lg bg-gradient-to-r from-slate-600 to-slate-700 p-2">
                              <Icon className="h-5 w-5 text-slate-200" />
                            </div>
                            <div>
                              <h4 className="mb-1 text-lg font-semibold text-slate-100">{dept.title}</h4>
                              <p className="mb-2 font-medium text-slate-300">{dept.email}</p>
                              <p className="text-sm text-slate-400">{dept.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      </div>
    </ThemeProvider>
  );
};

export default SpaceJetNetworkContact;
